import React from "react";
import { useLocalization } from "../../../../context/LocalizeContextProvider";
import Card from "../../../components/Card";
import TitlePage from "../../../components/TitlePage";
import SelectField from "../../../components/SelectField";
import SwitchField from "../../../components/SwitchField";
import Button from "../../../components/Button";
import Spinner from "../../../components/Spinner";
import styles from "./OperationalHours.module.css";

export default function OperationalHoursComponents({
  operatingHours,
  timeOptions,
  onDayChange,
  onSave,
  onCopyToAll,
  isLoading,
  isSaving,
}) {
  const localize = useLocalization();

  return (
    <Card>
      <Spinner loading={isLoading}>
        <div className={styles.header}>
          <TitlePage text={localize.getText("operatingHours")} />
          <p className={styles.subtitle}>
            {localize.getText("setOperatingHoursDesc")}
          </p>
        </div>

        <div className={styles.content}>
          {/* Action Bar - Quick Actions + Save Button */}
          <div className={styles.actionBar}>
            <div className={styles.quickActions}>
              <span className={styles.quickActionsLabel}>
                {localize.getText("quickActions")}:
              </span>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => {
                  operatingHours.forEach((day) => {
                    onDayChange(day.day_of_week, "is_closed", true);
                  });
                }}
                className={styles.quickActionBtn}
              >
                <i className="fas fa-times-circle mr-1"></i>
                {localize.getText("closeAll")}
              </Button>

              <Button
                variant="outline-success"
                size="sm"
                onClick={() => {
                  operatingHours.forEach((day) => {
                    if (day.day_of_week >= 2 && day.day_of_week <= 6) {
                      onDayChange(day.day_of_week, "is_closed", false);
                      onDayChange(day.day_of_week, "opening_time", "08:00");
                      onDayChange(day.day_of_week, "closing_time", "17:00");
                    }
                  });
                }}
                className={styles.quickActionBtn}
              >
                <i className="fas fa-business-time mr-1"></i>
                {localize.getText("workingDays")}
              </Button>
            </div>

            <Button
              variant="primary"
              onClick={onSave}
              disabled={isSaving}
              className={styles.saveButton}
            >
              {isSaving ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  {localize.getText("saving")}
                </>
              ) : (
                <>
                  <i className="fas fa-save mr-2"></i>
                  {localize.getText("save")}
                </>
              )}
            </Button>
          </div>

          {/* Operating Hours List */}
          <div className={styles.daysList}>
            {operatingHours.map((day, index) => (
              <div key={day.day_of_week} className={styles.dayCard}>
                <div className={styles.dayHeader}>
                  <div className={styles.dayInfo}>
                    <h6 className={styles.dayName}>{day.day_name}</h6>
                    <div className={styles.dayStatus}>
                      {day.is_closed ? (
                        <span className={styles.statusClosed}>
                          <i className="fas fa-times-circle mr-1"></i>
                          {localize.getText("closed")}
                        </span>
                      ) : day.opening_time && day.closing_time ? (
                        <span className={styles.statusOpen}>
                          <i className="fas fa-clock mr-1"></i>
                          {day.opening_time} - {day.closing_time}
                        </span>
                      ) : (
                        <span className={styles.statusPending}>
                          <i className="fas fa-exclamation-triangle mr-1"></i>
                          {localize.getText("notSetYet")}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className={styles.dayToggle}>
                    <SwitchField
                      value={!day.is_closed}
                      onChange={() =>
                        onDayChange(
                          day.day_of_week,
                          "is_closed",
                          !day.is_closed
                        )
                      }
                    />
                  </div>
                </div>

                {!day.is_closed && (
                  <div className={styles.timeSelectors}>
                    <div className={styles.timeField}>
                      <label className={styles.timeLabel}>
                        {localize.getText("openTime")}
                      </label>
                      <SelectField
                        placeholder={localize.getText("selectOpenTime")}
                        data={timeOptions}
                        value={day.opening_time ? day.opening_time : null}
                        onChange={(option) =>
                          onDayChange(
                            day.day_of_week,
                            "opening_time",
                            option || null
                          )
                        }
                        className={styles.timeSelect}
                      />
                    </div>

                    <div className={styles.timeDivider}>
                      <i className="fas fa-arrow-right"></i>
                    </div>

                    <div className={styles.timeField}>
                      <label className={styles.timeLabel}>
                        {localize.getText("closeTime")}
                      </label>
                      <SelectField
                        placeholder={localize.getText("selectCloseTime")}
                        data={timeOptions}
                        value={day.closing_time ? day.closing_time : null}
                        onChange={(option) =>
                          onDayChange(
                            day.day_of_week,
                            "closing_time",
                            option || null
                          )
                        }
                        className={styles.timeSelect}
                      />
                    </div>

                    {day.opening_time && day.closing_time && (
                      <div className={styles.copyButton}>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => onCopyToAll(day.day_of_week)}
                          className={styles.copyBtn}
                          title={localize.getText("copyToAllDays")}
                        >
                          <i className="fas fa-copy"></i>
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </Spinner>
    </Card>
  );
}
